import { useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import {
  btnSize,
  fontWeight,
  Heading,
  headingLevel,
  IconButton,
  useQuery,
  Text,
  textLevel,
  colors,
} from '../../../../../packages';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile, selectTreatmentPlans } from '../../../../../redux/modules/profile/selectors';
import { appointmentActionCreators } from '../../../../../redux/modules/appointment';
import ScheduleLink from '../../../schedule/components/schedule-link/ScheduleLink';
import { getMemberNotes } from '../../../../../services/member/member.service';
import CheckPDMP from '../../../../../components/v2/CheckPDMP';
import IdentificationCard from '../../../../../components/v2/IdentificationCard';
import RenderNotes from '../../../priorities/notes';
import Medications from '../../../priorities/medications';
import Diagnoses from '../../../priorities/diagnoses';
import EnviromentalFactors from '../../../priorities/enviromental-factors';
import SymptomaticFactors from '../../../priorities/symptomatic-factors';
import RenderEvaluations from '../../../priorities/evaluations';
import AppointmentWithEvals from '../../../priorities/appointments/AppointmentWithEvals';
import dayjs from 'dayjs';
import {
  getNextAppointment,
  getPreviousCompletedAppointment,
} from '../../../../../services/appointment/appointment.service';
import { PriorityDetailDrawer } from '../../../priorities/PriorityDetailDrawer';
import history from '../../../../../utils/history';
import { selectEvaluation } from '../../../../../redux/modules/conversation/selectors';
import AppointmentScheduling from '../../../appointments/components/appointment-scheduling';
import AppointmentDetail from '../../../appointments/components/appointment-detail';
import { sendMagicLink } from '../../../../../services/auth/auth.service';
import { useStyles } from './index.styles';
import { PrioritiesCard } from '../../../sessions/priority-list/PriorityList.constants';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import RecentCaseNotes from '../../../priorities/case-notes';
import { TreatmentPlansList } from './TreatmentPlansList';

const Dashboard = () => {
  const classes = useStyles();
  const { memberId } = useParams();
  const query = useQuery();
  const { isAdmin, meta } = useSelector(getAuth);
  const dispatch = useDispatch();
  const { demographicDetails = {}, selectedPatient } = useSelector(getProfile);
  const evaluations = useSelector(selectEvaluation);
  const treatmentPlans = useSelector(selectTreatmentPlans);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [notes, setNotes] = useState(null);
  const [caseNotesLoader, setCaseNotesLoader] = useState(false);
  const [viewAllType, setViewAllType] = useState(null);
  const [diagnosesReset, setDiagnosesReset] = useState(false);
  const [medicationsReset, setMedicationsReset] = useState(false);
  const [symptomsReset, setSymptomsReset] = useState(false);
  const [envFactorsReset, setEnvFactorsReset] = useState(false);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [viewAppointmentDetail, setViewAppointmentDetail] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [isAppointmentCreated, setIsAppointmentCreated] = useState(false);
  const [count, setCount] = useState({
    activeMedications: 1, // Start with 1 to allow initial render
  });
  const navigate = useNavigate();

  const onViewAll = type => {
    const name = query.get('name');
    if (name === 'dashboard') {
      navigate(`/${isAdmin ? 'admin' : 'provider'}/members/${memberId}?name=${type}`);
    }
    setSelectedPriority(null);
    setViewAllType(type);
  };

  const fetchNextAppointment = async () => {
    const { data } = await getNextAppointment(memberId, false);
    setNextAppointment(data.appointment);
  };

  const onElementsUpdated = type => {
    switch (type) {
      case PrioritiesCard.Diagnoses: {
        setDiagnosesReset(v => !v);
        break;
      }
      case PrioritiesCard.Medications: {
        setMedicationsReset(v => !v);
        break;
      }
      case PrioritiesCard.SymptomaticFactors: {
        setSymptomsReset(v => !v);
        break;
      }
      case PrioritiesCard.EnvironmentalFactors: {
        setEnvFactorsReset(v => !v);
        break;
      }
      default: {
        console.log('Unknown type updated');
      }
    }
  };

  const fetchNotesCall = () => {
    getMemberNotes({ userId: memberId }, { pageNumber: 0, pageSize: 3 })
      .then(res => {
        if (res.data?.patientContactNotes) {
          setNotes(res.data);
        }
        setCaseNotesLoader(false);
      })
      .catch(error => {
        setCaseNotesLoader(false);
        console.log({ error });
      });
  };

  useEffect(() => {
    setCaseNotesLoader(true);
    const appointmentsQueryParams = {
      orderBy: 'asc',
      page: 0,
      size: 3,
      sortBy: 'startTime',
      statuses: '',
      type: 'CURRENT',
      states: null,
      searchQuery: '',
    };
    if (memberId) {
      fetchNotesCall();
      dispatch(profileActionCreators.fetchDemographicMetadata());
      dispatch(profileActionCreators.fetchDemographicDetails(memberId));
      dispatch(profileActionCreators.fetchDomainTypes(memberId));
      dispatch(profileActionCreators.fetchMemberInsurance({ memberId }));
      dispatch(
        appointmentActionCreators.fetchAppointments({
          queryParams: appointmentsQueryParams,
        }),
      );
    }
  }, [memberId]);

  useEffect(() => {
    if (memberId) {
      getPreviousCompletedAppointment(memberId, dayjs().toISOString())
        .then(({ data }) => {
          setLastAppointment(data.appointment);
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [memberId]);

  useEffect(() => {
    dispatch(
      profileActionCreators.selectedPatient({
        member: {
          id: memberId,
          firstName: demographicDetails?.firstName || '',
          lastName: demographicDetails?.lastName || '',
          fullName: demographicDetails?.fullName || '',
          nickName: demographicDetails?.nickName || '',
          photo: null,
          uuid: demographicDetails?.uuid || '',
        },
        email: demographicDetails?.email || '',
        dob: demographicDetails?.dateOfBirth || '',
        phone: demographicDetails?.mobilePhone || '',
        fundsInWallet: 0,
        engagement: {
          Status: demographicDetails?.levelOfEngagementStatus || '',
          SubStatus: demographicDetails?.levelOfEngagementStatus || '',
        },
      }),
    );
  }, [demographicDetails]);

  useEffect(() => {
    void fetchNextAppointment();
  }, [isAppointmentCreated]);

  const sendMemberLink = async () => {
    try {
      const response = await sendMagicLink({ memberId });
      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Magic link sent successfully',
          }),
        );
      }
    } catch (err) {
      console.log(err);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: err.data?.errors?.[0]?.endUserMessage || 'Something went wrong',
        }),
      );
    }
  };

  const viewApptDetail = appt => {
    setSelectedAppointment(appt);
    setViewAppointmentDetail(true);
  };

  const onCloseViewAllDrawer = () => {
    setViewAllType(null);
    setSelectedPriority(null);
  };

  const onStartSession = appointment => {
    history.push(`/provider/appointments/current/${appointment?.appointmentId}/session`);
  };

  const onCloseDrawer = () => {
    setSelectedAppointment(null);
    setViewAppointmentDetail(false);
  };

  const toggleRescheduleDrawer = () => {
    setShowReschedule(!showReschedule);
  };

  const handleViewAllCaseNotes = () => {
    navigate(`/${isAdmin ? 'admin' : 'provider'}/members/${memberId}?name=notes`);
  };

  return (
    <Box className={classes.headingWrapper}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
          Dashboard
        </Heading>
        <IconButton icon="wand-outlined" size={btnSize.SMALL} onClick={sendMemberLink}>
          <Box className={classes.magicLink}>Send magic link</Box>
        </IconButton>
      </Box>
      <Box className={classes.flexColumn} sx={{ gap: 3, marginTop: '-16px' }}>
        <RenderNotes memberId={selectedPatient?.member?.id} hideIcon={false} />
        <RenderEvaluations isWaiting={!isMobile} memberId={selectedPatient?.member?.id} />
        {treatmentPlans && treatmentPlans.length > 0 && (
          <TreatmentPlansList
            onViewAll={() => {
              history.push(`/${isAdmin ? 'admin' : 'provider'}/members/${memberId}?name=treatment-plans`);
            }}
          />
        )}
        {nextAppointment && (
          <AppointmentWithEvals
            appointment={nextAppointment}
            fetchEvaluationsOnLoad
            isPast={false}
            title="Next appointment"
            hideNotes
            viewApptDetail={viewApptDetail}
            showViewDetails
          />
        )}
        <SymptomaticFactors
          memberId={selectedPatient?.member?.id}
          onViewAll={onViewAll}
          onClick={(item, type) => {
            setSelectedPriority(item);
            setViewAllType(type);
          }}
          reset={symptomsReset}
        />
        <RecentCaseNotes
          notes={notes?.patientContactNotes}
          total={notes?.totalRecords}
          memberId={memberId}
          fetchNotes={fetchNotesCall}
          caseNotesLoader={caseNotesLoader}
          onViewAll={handleViewAllCaseNotes}
        />
        {lastAppointment && (
          <AppointmentWithEvals
            appointment={lastAppointment}
            fetchEvaluationsOnLoad
            isPast
            hideNotes
            viewApptDetail={viewApptDetail}
            showViewDetails
          />
        )}
        <Diagnoses
          // title="Active diagnosis"
          memberId={selectedPatient?.member?.id}
          hideNotes
          onViewAll={onViewAll}
          onClick={(item, type) => {
            setSelectedPriority(item);
            setViewAllType(type);
          }}
          reset={diagnosesReset}
        />
        <EnviromentalFactors
          memberId={selectedPatient?.member?.id}
          onViewAll={onViewAll}
          onClick={(item, type) => {
            setSelectedPriority(item);
            setViewAllType(type);
          }}
          reset={envFactorsReset}
        />
        {count.activeMedications > 0 && (
          <Box className={classes.section}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 2.5,
                px: 3,
                borderBottom: `1px solid ${colors.dividerSecondary}`,
              }}
            >
              <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                Medications
              </Text>
              <Text
                level={textLevel.S}
                weight={fontWeight.SEMI_BOLD}
                color={colors.primary600}
                onClick={event => {
                  event.stopPropagation();
                  onViewAll('medications');
                }}
                sx={{ cursor: 'pointer' }}
              >
                View all
              </Text>
            </Box>

            <Box p={3}>
              <Medications
                memberId={selectedPatient?.member?.id}
                hideNotes={false}
                onViewAll={onViewAll}
                onClick={() => {}}
                reset={medicationsReset}
                isCardView={true}
                setCount={setCount}
              />
            </Box>
          </Box>
        )}

        <IdentificationCard isSession={false} memberId={selectedPatient?.member?.id} />
        {(demographicDetails?.state === 'Connecticut' ||
          demographicDetails?.state === 'Texas' ||
          demographicDetails?.state === 'Florida' ||
          demographicDetails?.state === 'Virginia' ||
          demographicDetails?.state === 'New Hampshire') && (
          <CheckPDMP
            patientState={demographicDetails?.state}
            uuid={demographicDetails?.uuid}
            summary={demographicDetails?.pdmpSummary}
            lastUpdated={demographicDetails?.pdmpLastUpdated}
          />
        )}
        {!isAdmin && (
          <ScheduleLink
            cardTitle="Scheduling link"
            copyLinkText="Copy scheduling link"
            generateLinkText="Generate scheduling link"
            mainLink={`book-appointment?memberId=${selectedPatient?.member?.id}&selectedProvider=${meta.userId}`}
          />
        )}
        {!!viewAllType && (
          <PriorityDetailDrawer
            memberId={selectedPatient?.member?.id}
            selectedItem={selectedPriority}
            type={viewAllType}
            onClose={onCloseViewAllDrawer}
            open={!!viewAllType}
            onElementsUpdated={onElementsUpdated}
          />
        )}
        <AppointmentDetail
          open={viewAppointmentDetail}
          onClose={onCloseDrawer}
          onRescheduleClick={toggleRescheduleDrawer}
          onAcceptAppointment={onStartSession}
          appointment={selectedAppointment}
          evaluations={evaluations}
        />
        <AppointmentScheduling
          open={showReschedule}
          onClose={toggleRescheduleDrawer}
          appointment={selectedAppointment}
          updateAppointment={setSelectedAppointment}
          setIsApptCreated={isCreated => setIsAppointmentCreated(isCreated)}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
